import { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { Pencil, Plus, Server as ServerIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../firebase/config';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { buildConnectCommand, DEFAULT_PORT } from '../../lib/servers';
import PageHeader from './shell/PageHeader';

const EMPTY = { name: '', region: '', host: '', port: '' };

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const ServerManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'servers'), orderBy('order', 'asc')));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load servers.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      region: s.region || '',
      host: s.host || '',
      port: s.port != null ? String(s.port) : '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host.trim()) {
      toast.error('Name and host are required.');
      return;
    }
    const portNum = form.port.trim() ? Number(form.port) : DEFAULT_PORT;
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      toast.error('Port must be a number between 1 and 65535.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        region: form.region.trim(),
        host: form.host.trim(),
        port: portNum,
        updatedAt: new Date(),
      };
      if (editing) {
        await updateDoc(doc(db, 'servers', editing.id), payload);
        toast.success('Server updated.');
      } else {
        await addDoc(collection(db, 'servers'), {
          ...payload,
          order: items.length,
          createdAt: new Date(),
        });
        toast.success('Server added.');
      }
      setDialogOpen(false);
      await fetchServers();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'servers', deleteTarget.id));
      toast.success('Server deleted.');
      setDeleteTarget(null);
      await fetchServers();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Site content"
        title="Game servers"
        description="The CS2KZ servers shown on the public /servers page. The connect command is derived from the host and port."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Add server
          </Button>
        }
      />

      {loading ? (
        <ul className="grid grid-cols-1 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <li
              key={i}
              className="h-24 animate-pulse rounded-md border border-border/60 bg-muted/40"
            />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Empty
          </p>
          <p className="mt-3 text-sm text-muted-foreground">No servers yet. Add your first one.</p>
          <div className="mt-5">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add server
            </Button>
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3">
          {items.map((s) => (
            <li key={s.id}>
              <Card className="border-border/60 bg-background/40 transition-colors hover:border-foreground/40">
                <CardContent className="flex items-start gap-5 pt-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/20">
                    <ServerIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-lg font-semibold">{s.name}</h3>
                    {s.region && (
                      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        {s.region}
                      </div>
                    )}
                    <code className="mt-2 inline-block font-mono text-xs text-muted-foreground">
                      {buildConnectCommand(s.host, s.port)}
                    </code>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(s)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit server' : 'Add server'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <Field label="Name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="KZCOMP EU 1"
                required
              />
            </Field>
            <Field label="Region">
              <Input
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                placeholder="Europe"
              />
            </Field>
            <Field label="Host" required hint="DNS name or IP — no port here.">
              <Input
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="cs2kz.kzcomp.com"
                required
              />
            </Field>
            <Field label="Port" hint="Defaults to 27015. Always shown in the connect command.">
              <Input
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
                placeholder="27015"
                min="1"
                max="65535"
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete server?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> from the
            public servers page.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServerManagement;
